import React from 'react';
import { ArrowRight, Mail } from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';
import useSeo from '../hooks/useSeo';
import { IMAGES, LEATHER, COMPANY } from '../mock';

const StatusPill = ({ s, light }) => {
  const tone = s === 'Granted' ? 'border-emerald-500/60 text-emerald-600' : s === 'Next Required' ? 'border-[#6b6f76]/50 text-[#6b6f76]' : 'border-[#c6a15b]/60 text-[#a3813f]';
  return <span className={`text-[10px] tracking-[0.18em] uppercase border px-3 py-1 whitespace-nowrap ${tone} ${light && s !== 'Granted' ? 'text-[#d9bd82]' : ''}`}>{s}</span>;
};

const Head = ({ num, eyebrow, title, light, children }) => (
  <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 reveal">
    <div className="lg:col-span-7">
      <p className={`eyebrow ${light ? 'eyebrow-light' : ''} mb-5`}>{num} / {eyebrow}</p>
      <h2 className={`font-serif text-4xl md:text-6xl leading-[1.02] ${light ? 'text-[#f7f4ee]' : 'text-[#0a1f38]'}`}>{title}</h2>
    </div>
    {children && <p className={`lg:col-span-5 font-light leading-relaxed text-lg ${light ? 'text-white/60' : 'text-[#3a3f47]'}`}>{children}</p>}
  </div>
);

const Leather = () => {
  useScrollReveal();
  useSeo({ title: 'LRG Leather Botswana, Lobatse Leather Industrial Platform', description: 'A SEZA licensed tanning and leather manufacturing export platform in Lobatse, Botswana, structured by LRG Holdings with Italian technical partner Italprogetti and open to anchor investors.', path: '/leather', image: '/images/community-botswana-juniors.jpg' });
  const L = LEATHER;

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden" data-testid="leather-hero">
        <img src={IMAGES.leatherTexture} alt="Leather texture" className="absolute inset-0 w-full h-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a0d08]/90 via-[#1a0d08]/70 to-[#071627]/40" />
        <div className="relative container-lg pt-32 pb-24">
          <p className="eyebrow eyebrow-line eyebrow-light mb-8 reveal is-visible">LRG Holdings / Lobatse, Botswana / SEZA-Licensed ({L.licence})</p>
          <h1 className="font-serif text-[#f7f4ee] text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.98] max-w-4xl">Botswana Leather Industrial Platform.</h1>
          <p className="font-serif text-[#d9bd82] text-2xl md:text-3xl mt-8 max-w-2xl leading-snug">Structuring a private-sector led tanning and manufacturing export base in Lobatse, Botswana.</p>
          <p className="text-white/70 font-light mt-6 max-w-xl">Led by LRG Holdings in coordination with SEZA and international manufacturing partners.</p>
          <div className="flex flex-wrap items-center gap-6 mt-12">
            <a href={`mailto:${COMPANY.email}?subject=LRG%20Leather%20Botswana`} className="btn-gold" data-testid="leather-engage-button">Engage With LRG <ArrowRight size={15} /></a>
            <span className="text-white/55 text-xs tracking-[0.18em] uppercase border-l border-white/30 pl-6">Phase 1 structuring now underway</span>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0a1f38] border-b border-white/10">
        <div className="container-lg grid grid-cols-2 lg:grid-cols-5 ledger-grid dark" data-testid="leather-stats">
          {L.stats.map((s) => (
            <div key={s.value} className="p-8 reveal">
              <p className="font-serif text-4xl md:text-5xl text-[#f7f4ee]">{s.value}</p>
              <p className="text-white/50 text-sm font-light mt-3">{s.label}</p>
            </div>
          ))}
        </div>
        <p className="container-lg text-white/35 text-[11px] tracking-[0.14em] uppercase py-4">Sources: Statistics Botswana, S&amp;P Global Ratings, Moody&rsquo;s Investors Service, SEZA</p>
      </section>

      {/* 01 Why */}
      <section className="py-28 bg-[#f7f4ee]">
        <div className="container-lg">
          <Head num="01" eyebrow="Why This Exists" title="Why This Exists." />
          <div className="grid md:grid-cols-3 ledger-grid">
            {L.why.map((w, i) => (
              <div key={w.title} className="p-10 reveal" style={{ transitionDelay: `${i * 100}ms` }}>
                <span className="numeral text-3xl">0{i + 1}</span>
                <h3 className="font-serif text-3xl text-[#0a1f38] mt-8 mb-5">{w.title}</h3>
                {w.lines.map((l) => <p key={l} className="text-[#3a3f47] font-light text-lg leading-relaxed">{l}</p>)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 02 Project */}
      <section className="py-28 bg-[#1a1d21]">
        <div className="container-lg">
          <Head num="02" eyebrow="The Project" title="The Project." light>
            A vertically integrated leather processing and manufacturing platform based in Lobatse, within the SEZA industrial framework.
          </Head>
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-4 reveal">
              <p className="eyebrow eyebrow-light mb-5">Structure</p>
              <p className="font-serif text-5xl text-[#f7f4ee]">SPV</p>
              <p className="font-serif italic text-2xl text-[#d9bd82] mt-1">LRG Leather Botswana</p>
              <div className="img-zoom mt-10 h-64"><img src={IMAGES.leatherCraft} alt="Leather craftsmanship" className="w-full h-full object-cover" /></div>
            </div>
            <div className="lg:col-span-8 reveal" style={{ transitionDelay: '120ms' }}>
              <p className="eyebrow eyebrow-light mb-5">Scope</p>
              <ul className="divide-y divide-white/10 border-y border-white/10">
                {L.scope.map((s, i) => (
                  <li key={s} className="flex items-center gap-6 py-6"><span className="numeral text-[#d9bd82] text-xl">0{i + 1}</span><span className="font-serif text-2xl md:text-3xl text-[#f7f4ee]">{s}</span></li>
                ))}
              </ul>
              <p className="eyebrow eyebrow-light mt-14 mb-5">Current Status</p>
              <ul className="divide-y divide-white/10 border-y border-white/10" data-testid="leather-status">
                {L.status.map(([t, s], i) => (
                  <li key={t} className="flex items-center gap-6 py-5"><span className="numeral text-[#d9bd82] text-lg">0{i + 1}</span><span className="flex-1 text-white/80 font-light">{t}</span><StatusPill s={s} light /></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 03 Why Botswana + Environment */}
      <section className="py-28 bg-[#efe9df]">
        <div className="container-lg">
          <Head num="03" eyebrow="Environmental and Social Design" title="Designed to Leather Working Group standards from Phase 1.">
            Environmental engineering decisions will be locked with the technical partner at design freeze. Several remain open by intent because they follow end-market specification.
          </Head>
          <div className="grid md:grid-cols-2 ledger-grid">
            {L.environment.map((e, i) => (
              <div key={e.title} className="p-10 reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="flex items-center justify-between mb-8"><span className="numeral text-3xl">0{i + 1}</span><StatusPill s={e.tag} /></div>
                <h3 className="font-serif text-3xl text-[#0a1f38] mb-4">{e.title}</h3>
                <p className="text-[#6b6f76] font-light leading-relaxed">{e.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 04 Investment */}
      <section className="py-28 bg-[#0a1f38]" data-testid="leather-investment">
        <div className="container-lg grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 reveal">
            <p className="eyebrow eyebrow-light mb-5">04 / Investment Opportunity</p>
            <h2 className="font-serif text-4xl md:text-6xl text-[#f7f4ee] leading-[1.02]">Investment Opportunity.</h2>
            <div className="glass-card p-8 mt-12">
              <p className="eyebrow eyebrow-light mb-3">{L.investment.label}</p>
              <p className="font-serif text-5xl text-[#f7f4ee]">{L.investment.round}</p>
              <p className="text-white/60 font-light leading-relaxed mt-6">{L.investment.text}</p>
            </div>
          </div>
          <div className="lg:col-span-7 reveal" style={{ transitionDelay: '120ms' }}>
            <p className="eyebrow eyebrow-light mb-5">Revenue Drivers</p>
            <ul className="divide-y divide-white/10 border-y border-white/10 mb-14">
              {L.investment.drivers.map((d, i) => <li key={d} className="flex items-center gap-6 py-5"><span className="numeral text-[#d9bd82] text-lg">0{i + 1}</span><span className="font-serif text-2xl text-[#f7f4ee]">{d}</span></li>)}
            </ul>
            <p className="eyebrow eyebrow-light mb-5">Partnership Model</p>
            <div className="grid sm:grid-cols-2 ledger-grid dark">
              {L.partners.map((p) => (
                <div key={p.code} className="p-7">
                  <span className="font-serif text-[#d9bd82] text-xl">{p.code}</span>
                  <h4 className="font-serif text-2xl text-[#f7f4ee] mt-4">{p.name}</h4>
                  <p className="text-white/55 font-light text-sm mt-2">{p.role}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 05 Technical partner */}
      <section className="py-28 bg-[#f7f4ee]">
        <div className="container-lg">
          <Head num="05" eyebrow="Technical Partners" title="Italian engineering, under signed agreement.">
            A deliberate choice to align the platform with premium end-market specification from day one.
          </Head>
          <div className="grid lg:grid-cols-12 ledger-grid">
            <div className="lg:col-span-4 p-10 reveal">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#a3813f] mb-6">Italprogetti</p>
              <h3 className="font-serif text-4xl text-[#0a1f38]">{L.technical.name}</h3>
              <p className="font-serif italic text-xl text-[#a3813f] mt-1">{L.technical.sub}</p>
              <dl className="mt-10 space-y-3">
                {L.technical.facts.map(([k, v]) => <div key={k} className="flex justify-between border-b border-[#0a1f38]/10 pb-2 text-sm"><dt className="text-[#6b6f76]">{k}</dt><dd className="text-[#0a1f38]">{v}</dd></div>)}
              </dl>
            </div>
            <div className="lg:col-span-8 p-10 reveal" style={{ transitionDelay: '120ms' }}>
              <p className="eyebrow mb-4">Phase 1 Engineering and Plant Design</p>
              <p className="text-[#3a3f47] font-light text-lg leading-relaxed">{L.technical.text}</p>
              <p className="text-[#6b6f76] font-light text-sm mt-6">Technical partnership agreement signed. Detailed engineering follows anchor off-take specification.</p>
              <div className="flex flex-wrap gap-3 mt-8">
                {L.technical.tags.map((t) => <span key={t} className="text-[11px] tracking-[0.14em] uppercase border border-[#0a1f38]/25 px-3 py-1.5 text-[#0a1f38]">{t}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 06 Progress */}
      <section className="py-28 bg-[#efe9df]" data-testid="leather-progress">
        <div className="container-lg">
          <Head num="06" eyebrow="Transparency" title="Current progress.">
            We distinguish between what is confirmed and what is proposed. No overstatement. Status as of the current structuring phase.
          </Head>
          <ul className="divide-y divide-[#0a1f38]/15 border-y border-[#0a1f38]/15 reveal">
            {L.progress.map(([t, s]) => (
              <li key={t} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 py-5"><StatusPill s={s} /><span className="text-[#0a1f38] font-light text-lg">{t}</span></li>
            ))}
          </ul>
        </div>
      </section>

      {/* Sponsor + CTA */}
      <section className="relative py-32 overflow-hidden" data-testid="leather-cta">
        <img src={IMAGES.cattle} alt="Botswana cattle" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#071627]/85" />
        <div className="relative container-lg grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 reveal">
            <p className="eyebrow eyebrow-light mb-5">Project Sponsor</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#f7f4ee] leading-tight">{COMPANY.founder} is the founder of LRG Holdings, the developer and operating entity behind the Botswana Leather Industrial Platform.</h2>
            <p className="text-white/65 font-light leading-relaxed mt-6 max-w-2xl">LRG Holdings is a citizen-owned enterprise focused on building globally competitive platforms from African supply chains.</p>
          </div>
          <div className="lg:col-span-5 glass-card p-10 reveal" style={{ transitionDelay: '120ms' }}>
            <p className="eyebrow eyebrow-light mb-6">Engage With the Platform</p>
            <ul className="space-y-3 text-[#f7f4ee] font-serif text-2xl">{['Strategic investors', 'Technical and manufacturing partners', 'Institutional stakeholders'].map((x, i) => <li key={x}><span className="numeral text-[#d9bd82] text-lg mr-4">0{i + 1}</span>{x}</li>)}</ul>
            <div className="flex flex-wrap gap-4 mt-10">
              <a href={`mailto:${COMPANY.email}?subject=LRG%20Leather%20Botswana`} className="btn-gold"><Mail size={15} /> Contact LRG</a>
              <a href={L.site} target="_blank" rel="noopener noreferrer" className="btn-outline btn-outline-light" data-testid="leather-site-link">Full Investor Site</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Leather;
